import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/studio";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return setError("Password must be at least 6 characters");
    setLoading(true);
    setError("");
    const r = await register(email, password, name || undefined);
    setLoading(false);
    if (r.ok) {
      toast.success("Account created");
      nav(next);
    } else {
      setError(r.error || "Sign-up failed");
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-[1200px] items-center px-6 py-12">
      <div className="grid w-full gap-0 md:grid-cols-2">
        <div className="border border-[#222] p-10">
          <Link to="/" className="font-display text-4xl tracking-widest">
            <span className="text-[#FF003C]">V</span>OLT<span className="text-[#FFE800]">.</span>MV
          </Link>
          <h1 className="mt-12 font-display text-6xl leading-none">
            JOIN THE
            <br />
            <span className="text-[#FFE800]">RIOT.</span>
          </h1>
          <p className="mt-6 max-w-sm text-[#c8c8c8]">
            Create an account to make AI music videos with your own tracks and photos.
          </p>
        </div>
        <div className="border border-l-0 border-[#222] bg-[#0A0A0B] p-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFE800]">// SIGN UP</div>
          <div className="mt-2 font-display text-4xl">Create Account</div>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            <Field label="Display Name (optional)">
              <input
                data-testid="register-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-[#222] bg-[#050505] p-3 font-mono text-sm text-white outline-none focus:border-[#FFE800]"
                placeholder="Alex"
              />
            </Field>
            <Field label="Email">
              <input
                data-testid="register-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-[#222] bg-[#050505] p-3 font-mono text-sm text-white outline-none focus:border-[#FFE800]"
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Password (min 6)">
              <input
                data-testid="register-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-[#222] bg-[#050505] p-3 font-mono text-sm text-white outline-none focus:border-[#FFE800]"
                placeholder="••••••••"
              />
            </Field>
            {error && (
              <div className="border border-[#FF003C] bg-[#FF003C]/10 p-3 font-mono text-xs text-[#FF003C]" data-testid="register-error">
                {error}
              </div>
            )}
            <button data-testid="register-submit" type="submit" disabled={loading} className="btn-brutal w-full">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
              Create Account
            </button>
          </form>
          <div className="mt-8 font-mono text-xs uppercase tracking-widest text-[#808080]">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-login-link" className="text-white hover:text-[#FF003C]">
              Sign in →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-[#808080]">{label}</div>
      {children}
    </label>
  );
}
